import { WalletIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { FeatureOff } from "@/components/FeatureOff"
import { DocumentStatusBadge } from "@/features/invoices/DocumentStatusBadge"
import { PaymentStateBadge } from "@/features/payments/PaymentStateBadge"
import { isFeatureOff, useOutstanding } from "@/features/payments/use-payments"
import { SyncStatus } from "@/components/SyncStatus"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ApiError } from "@/lib/api-error"
import { useFormatDay } from "@/lib/dates"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * The bill register, showing only what is still owed — `/invoices?unpaid=true`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT IS A DIFFERENT REQUEST, NOT A NARROWER ONE, AND THAT IS FORCED.
 *
 * "Unpaid" is `grand_total_paise` − `SUM(payments.amount_paise)`, and no `where`
 * on `GET /invoices` can express it: Prisma cannot filter on an aggregate of a
 * related table, and the register's `where` is built inline in the route. So the
 * server answers this question from `GET /payments/outstanding` — the SAME
 * producer, `readOutstandingInvoices`, that feeds the home screen's total. One
 * definition of "owed", serving the figure and the list it opens.
 *
 * THE ALTERNATIVE WOULD HAVE BEEN A LIE. Fetching one page of the register and
 * hiding the settled rows in the client looks exactly like a working filter, and
 * silently misses every unpaid bill on the pages it has not fetched. That is the
 * failure `list-filter-parity.test.ts` exists for.
 *
 * SO THE FILTER IS EXCLUSIVE, AND THE SCREEN SAYS SO BY SHOWING NOTHING ELSE.
 * While it is on, the search box, the status select and the "More filters" sheet
 * are not rendered — they filter the other request and would sit there looking
 * combinable while doing nothing. Turning it on clears the URL of everything
 * else; turning it off restores the ordinary register.
 *
 * NO PAGING, DELIBERATELY. The endpoint sends the whole position because the
 * point of the figure is that it is complete: a total over page 1 of 4 is a
 * smaller number than the truth, presented as the truth.
 *
 * "AGE", NEVER "OVERDUE". `ageDays` is whole days since the invoice date. This
 * system models no credit terms and stores no due date, so nothing here may call
 * a bill late — that would be the app inventing an agreement the business never
 * made.
 *
 * THE STATUS BADGE STAYS, IN BOTH LAYOUTS. Every row here is ISSUED — that is
 * what makes it owable (§17.5 rule A) — and the payment badge sits BESIDE it
 * rather than in its place, because being paid is a fact about the bank and not
 * about the document (§17.5). This screen draws two layouts, phone cards and a
 * desktop table, and the rule is about the SCREEN: a claim that holds in the
 * `md:block` half and not the `md:hidden` one is false on the device the owner
 * actually bills from. jsdom applies no media queries, so a test that reaches
 * for either badge with `getAllByText` is satisfied by the other layout — the
 * assertions therefore look inside one layout at a time.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function UnpaidRegister(): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const query = useOutstanding()

  /*
   * §18.3 — A 404 HERE MEANS THE MODULE IS OFF, NOT THAT SOMETHING IS MISSING.
   * Every payment route sits behind `requireFlag("payments")`, which runs before
   * `requireCap` and answers 404 rather than 403 so "forbidden" cannot leak the
   * existence of unreleased work. This branch is the client half of that
   * contract: it must not say "you do not have permission", and it offers no
   * retry, because pressing a button cannot switch a flag on.
   */
  if (query.isError && isFeatureOff(query.error)) {
    /*
     * ONE COMPONENT AND ONE PAIR OF STRINGS FOR ALL FIVE FLAGGED MODULES —
     * `components/FeatureOff.tsx`, which replaced the two families of wording
     * this screen used to carry its own copy of. See that file for why the
     * module name is a parameter rather than five sentences.
     */
    return <FeatureOff module={t("payment.moduleName")} icon={<WalletIcon />} />
  }

  if (query.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>
            {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
          </span>
          <Button
            variant="outline"
            onClick={() => {
              void query.refetch()
            }}
          >
            {t("btn.retry")}
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  if (query.isPending) return <RegisterSkeleton />

  const { data: rows, byCustomer, totals } = query.data

  if (rows.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <WalletIcon />
          </EmptyMedia>
          <EmptyTitle>{t("payment.allSettledTitle")}</EmptyTitle>
          <EmptyDescription>{t("payment.allSettledHelp")}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        * THE ANSWER FIRST. He came here from a card that said ₹3,42,150; the
        * same figure has to be the first thing on the screen it opened, or the
        * two look like different numbers. It is the server's `totals`, not a sum
        * of the rows below — §6, and the one place a client sum could disagree.
        */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-2xl font-semibold tabular-nums">
            {formatRupees(totals.outstandingPaise)}
          </p>
          <p className="text-muted-foreground text-sm">
            {t("payment.owedAcross", { count: totals.invoiceCount })}
          </p>
        </div>
        <SyncStatus
          updatedAt={query.dataUpdatedAt}
          syncing={query.isFetching}
          onSync={() => {
            void query.refetch()
          }}
        />
      </div>

      {/*
        * Phone: one card per bill, the whole row a tap target.
        *
        * BOTH BADGES HERE TOO, IN THE SAME ORDER AS THE TABLE BELOW. This is the
        * owner's primary device (§11.1), and the badge slot on this card is the
        * one the ordinary register at `/invoices` fills with "Issued". A card
        * showing only "Partly paid" would put a payment word in the document's
        * place — the same pill, the same tone map, the same position, a
        * different vocabulary and nothing on screen marking the switch. That is
        * exactly the overloading §17.5 refuses in the schema, reproduced in the
        * layout the owner actually uses.
        */}
      <Card data-testid="unpaid-phone-list" className="overflow-hidden py-0 md:hidden">
        <ul className="flex flex-col">
          {rows.map((row) => (
            <li key={row.invoiceId} className="border-b last:border-b-0">
              <Link
                to={routes.invoice(row.invoiceId)}
                className="hover:bg-accent/50 flex min-h-14 items-center gap-3 p-4 transition-colors"
              >
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium tabular-nums">{row.invoiceNumber}</span>
                    {/* The document stands, and the money has not all arrived.
                      * Two facts, two badges — never one badge for both. */}
                    <DocumentStatusBadge status="ISSUED" kind="invoice" className="text-xs" />
                    <PaymentStateBadge state={row.state} className="text-xs" />
                  </span>
                  <span className="text-muted-foreground truncate text-sm">
                    {row.customerName ?? t("lbl.customerUnknown")}
                  </span>
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {formatDay(row.invoiceDate)} · {t("payment.ageDays", { count: row.ageDays })}
                  </span>
                </span>
                <span className="shrink-0 font-medium tabular-nums">
                  {formatRupees(row.outstandingPaise)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Card>

      {/* Desktop: the register as a book, with what came in beside what is left. */}
      <Card data-testid="unpaid-desktop-table" className="hidden overflow-hidden py-0 md:block">
        <Table>
          <TableHeader>
            <TableRow>
              {/*
                * NOT SORTABLE, and the plain headers say so. `SortableHead`
                * everywhere else drives `?sort=` on a route that supports it;
                * `GET /payments/outstanding` takes no parameters at all, so a
                * header that looked sortable and was not would be worse than a
                * plain one. The rows arrive OLDEST BILL FIRST, which is the
                * order the question is usually asked in.
                */}
              <TableHead>{t("lbl.number")}</TableHead>
              <TableHead>{t("lbl.date")}</TableHead>
              <TableHead>{t("lbl.customer")}</TableHead>
              <TableHead>{t("lbl.status")}</TableHead>
              <TableHead className="text-right">{t("payment.billTotal")}</TableHead>
              <TableHead className="text-right">{t("payment.received")}</TableHead>
              <TableHead className="text-right">{t("payment.stillOwed")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.invoiceId} className="hover:bg-accent/50 relative cursor-pointer">
                <TableCell className="font-medium tabular-nums">
                  {/* `relative` on the row is load-bearing — the stretched
                    * overlay resolves against the nearest positioned ancestor,
                    * and without it every click on the page opens the last row. */}
                  <Link
                    to={routes.invoice(row.invoiceId)}
                    className="after:absolute after:inset-0 hover:underline"
                  >
                    {row.invoiceNumber}
                  </Link>
                </TableCell>
                <TableCell className="tabular-nums whitespace-nowrap">
                  {formatDay(row.invoiceDate)}
                </TableCell>
                <TableCell className="max-w-[24ch] truncate">
                  {row.customerName ?? (
                    <span className="text-muted-foreground">{t("lbl.customerUnknown")}</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="flex flex-wrap items-center gap-1.5">
                    {/* ISSUED, and partly paid. Two facts, two badges — the
                      * payment word never replaces the document's status. */}
                    <DocumentStatusBadge status="ISSUED" kind="invoice" />
                    <PaymentStateBadge state={row.state} />
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums whitespace-nowrap">
                  {formatRupees(row.grandTotalPaise)}
                </TableCell>
                <TableCell className="text-right tabular-nums whitespace-nowrap">
                  {formatRupees(row.receivedPaise)}
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums whitespace-nowrap">
                  {formatRupees(row.outstandingPaise)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/*
        * WHO owes it, which is the question actually asked when the phone rings.
        * The API folds this from the same rows — not a second query and not a
        * client-side group-by — so the parts add up to the total above by
        * construction. Each name opens that customer's statement.
        */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("payment.byCustomerTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-1">
          <ul className="flex flex-col divide-y">
            {byCustomer.map((entry) => (
              <li key={entry.customerId}>
                <Link
                  to={routes.customerStatement(entry.customerId)}
                  className="hover:bg-accent/50 flex min-h-11 items-center gap-3 rounded-md px-2 py-2 transition-colors"
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium">
                      {entry.customerName ?? t("lbl.customerUnknown")}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {t("payment.billsCount", { count: entry.invoiceCount })}
                    </span>
                  </span>
                  <span className="shrink-0 font-medium tabular-nums">
                    {formatRupees(entry.outstandingPaise)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}

function RegisterSkeleton(): React.JSX.Element {
  return (
    <Card className="overflow-hidden py-0">
      <ul className="flex flex-col">
        {[0, 1, 2, 3, 4].map((index) => (
          <li key={index} className="flex items-center gap-3 border-b p-4 last:border-b-0">
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-40" />
            </div>
            <Skeleton className="h-4 w-24" />
          </li>
        ))}
      </ul>
    </Card>
  )
}
